/**
 * F&B 입점 업체 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { fnbService } from '../../services/fnbService';
import { FnbVendor, FnbVendorFilter } from '../../types/fnb';

const FnbVendorsList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FnbVendorFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['fnbVendors', filter],
    queryFn: () => fnbService.getVendors(filter),
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const columns: Column<FnbVendor>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'vendor_name',
      label: '업체명',
      render: (vendor) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4' }}
          onClick={() => navigate(`/admin/fnb/vendors/${vendor.id}`)}
        >
          {vendor.vendor_name}
        </span>
      ),
    },
    {
      key: 'vendor_code',
      label: '업체 코드',
      render: (vendor) => (
        <span style={{ color: '#ccc' }}>{vendor.vendor_code}</span>
      ),
    },
    {
      key: 'vendor_type',
      label: '업체 유형',
      render: (vendor) => (
        <span style={{ color: '#ccc' }}>{vendor.vendor_type || '-'}</span>
      ),
    },
    {
      key: 'location',
      label: '위치',
      render: (vendor) => (
        <span style={{ color: '#ccc' }}>{vendor.location || '-'}</span>
      ),
    },
    {
      key: 'commission_rate',
      label: '수수료율',
      render: (vendor) => (
        <span style={{ color: '#00BCD4', fontWeight: 'bold' }}>
          {vendor.commission_rate}%
        </span>
      ),
    },
    {
      key: 'contract_period',
      label: '계약 기간',
      render: (vendor) => (
        <span style={{ color: '#ccc' }}>
          {vendor.contract_start && vendor.contract_end ? (
            <>
              {new Date(vendor.contract_start).toLocaleDateString('ko-KR')} ~ {new Date(vendor.contract_end).toLocaleDateString('ko-KR')}
            </>
          ) : (
            '-'
          )}
        </span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (vendor) => getStatusBadge(vendor.status),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '업체명, 코드로 검색',
    },
    {
      name: 'vendor_type',
      label: '업체 유형',
      type: 'text',
      placeholder: '업체 유형으로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (vendor: FnbVendor) => {
    navigate(`/admin/fnb/vendors/${vendor.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>F&B 입점 업체 관리</h1>
          <Button variant="primary" onClick={() => navigate('/admin/fnb/vendors/new')}>
            업체 등록
          </Button>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.vendors || []}
              loading={isLoading}
              emptyMessage="F&B 업체가 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default FnbVendorsList;

